export interface AudienceCount {
  key: string;
  hits: number;
}

export interface AudienceTrendPoint {
  bucket: string;
  hits: number;
}

/** The part of /analytics/StreamSummary the Audience page uses. */
export interface AudienceSummary {
  label: string;
  bucket: string;
  totalHits: number;
  uniqueVisitors: number;
  newVisitorHits: number;
  returningVisitorHits: number;
  referredHits: number;
  directHits: number;
  firstHit: string | null;
  lastHit: string | null;
  trend: AudienceTrendPoint[];
  topCities: AudienceCount[];
  topReferrals: AudienceCount[];
  topDeviceTypes: AudienceCount[];
  topOperatingSystems: AudienceCount[];
  topBrowsers: AudienceCount[];
}

export const AUDIENCE_DAY_OPTIONS = [1, 3, 7, 14, 30, 90];

/** Minutes east of UTC for this browser, which the API uses to put hours and days on the viewer's clock. */
export const viewerOffsetMinutes = (): number => -new Date().getTimezoneOffset();

export const audienceUrl = (apiBase: string, stream: string, uid: string, days: number, offsetMinutes: number): string =>
  `${apiBase}/analytics/StreamSummary?stream=${encodeURIComponent(stream)}&uid=${encodeURIComponent(uid)}&days=${days}&tzOffsetMinutes=${offsetMinutes}`;

/** The busiest bucket, or null when nobody visited. Ties go to the earliest. */
export const peakBucket = (trend: AudienceTrendPoint[]): AudienceTrendPoint | null => {
  let best: AudienceTrendPoint | null = null;
  for (const point of trend) if (point.hits > 0 && (!best || point.hits > best.hits)) best = point;
  return best;
};

/** Share of `part` in `whole`, as a whole percent (0 when `whole` is 0). */
export const percent = (part: number, whole: number): number => (whole > 0 ? Math.round((part / whole) * 100) : 0);

/**
 * "2026-10-27T14:00" -> a short label. Hourly buckets read "Tue 2 PM"; day buckets "Oct 27".
 * Parsed by hand so the label never shifts with the browser's time zone.
 */
export const bucketLabel = (bucket: string, locale?: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):\d{2})?/.exec(bucket);
  if (!match) return bucket;
  const [, y, m, d, h] = match;
  const date = new Date(Date.UTC(+y, +m - 1, +d, h ? +h : 0));
  if (h === undefined) return date.toLocaleDateString(locale, { timeZone: "UTC", month: "short", day: "numeric" });
  return date.toLocaleString(locale, { timeZone: "UTC", weekday: "short", hour: "numeric" });
};

/** Hits for the first `limit` entries, with the rest folded into "Other", dropping an empty key's name. */
export const topWithOther = (items: AudienceCount[], limit: number, unknownLabel: string, otherLabel: string): AudienceCount[] => {
  const named = items.map((i) => ({ key: i.key || unknownLabel, hits: i.hits })).sort((a, b) => b.hits - a.hits);
  if (named.length <= limit) return named;
  const rest = named.slice(limit).reduce((sum, i) => sum + i.hits, 0);
  return [...named.slice(0, limit), { key: otherLabel, hits: rest }];
};
